/* eslint-disable @typescript-eslint/no-explicit-any */
import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import callApi from 'lib/callApi';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';

type Props = {
  id: any;
  onClose: () => void;
};

const FindingDetailModal = ({ id, onClose }: Props) => {
  const [finding, setFinding] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const processIgnoreMessage = (json) => {
    let ignoredInfo = '';
    if (json.is_ignored) {
      ignoredInfo += 'Yes';
      if (json.ignored_date || json.ignored_by) {
        ignoredInfo += ` (${
          json.ignored_date
            ? 'Ignored at ' + moment(json.ignored_date).format('lll') + ', '
            : ''
        }${json.ignored_by ? 'by ' + json.ignored_by : ''})`;
      }
    } else {
      ignoredInfo += 'No';
      if (json.unignored_date || json.unignored_by) {
        ignoredInfo += ` (${
          json.unignored_date
            ? 'Unignored at ' + moment(json.unignored_date).format('lll') + ', '
            : ''
        }${json.unignored_by ? 'by ' + json.unignored_by : ''})`;
      }
    }
    if (json.is_ignored) {
      ignoredInfo += `<br/>Reason: ${
        json.ignored_reason || ''
      } <br/> Comments and Details: ${json.ignored_comments || ''}`;
    }
    return ignoredInfo;
  };

  const processJSON = (json) => {
    if (!json && Object.keys(json).length === 0) return null;
    let webAppJson = {};
    let commonJson = {};
    // Process json for Web Apps Data Here
    if (json.source_type === 'was_data') {
      // Web App: Add new access_path key and assign the Results->accessPath.list
      const accessPathList =
        Object.keys(json.result_list).length &&
        json.result_list.list[0].Result?.accessPath?.list;

      // Web App: Add new payload key and flat request fields to the PayloadInstance object
      const payloadList = Object.keys(json.result_list).length
        ? json.result_list.list[0].Result?.payloads?.list
        : null;
      const payloadFlatList = payloadList
        ? payloadList.map((payloadObj) => {
            const { link, method, headers } =
              payloadObj.PayloadInstance.request;
            let payload = payloadObj.PayloadInstance.payload;
            payload = payload && payload !== 'N/A' ? payload : undefined;

            return {
              PayloadInstance: {
                payload: payload && payload,
                request: {
                  link,
                  method,
                  headers,
                },
                response: payloadObj.PayloadInstance.response,
              },
            };
          })
        : null;
      // Web App: convert cwe array to list of object with url and id
      const cwe = json.cwe;
      const cweListUrl = cwe
        ? cwe.map((id) => {
            return {
              id: `CWE-${id}`,
              url: `https://cwe.mitre.org/data/definitions/${id}.html`,
            };
          })
        : [];
      // Web App: convert owasp, wasc array to proper list format
      const owaspList = json.owasp;
      const wascList = json.wasc;

      webAppJson = {
        payload: { list: payloadFlatList },
        access_path: { list: accessPathList },
        owasp: { list: owaspList },
        wasc: { list: wascList },
        cwe: cweListUrl,
      };
    }
    // Common: Ignore comment
    let ignoreMessage = processIgnoreMessage(json);

    // Common: Severity 1: Minimal 2: Medium 3: Serious 4: Critical 5: Urgent
    let severity;
    switch (json?.severity) {
      case 1:
        severity = 'Minimal';
        break;
      case 2:
        severity = 'Medium';
        break;
      case 3:
        severity = 'Serious';
        break;
      case 4:
        severity = 'Critical';
        break;
      case 5:
        severity = 'Urgent';
        break;
      default:
        severity = null;
    }

    commonJson = {
      severity: severity,
      ignore_message: ignoreMessage,
    };

    // Create the final processed object
    const processedObj = {
      ...json,
      // Web Apps
      ...webAppJson,
      //   Common
      ...commonJson,
    };

    return processedObj;
  };

  // useEffect
  useEffect(() => {
    if (!id || id === 0) return;
    setLoading(true);
    callApi(`findings/${id}/`, 'GET')
      .then((res) => {
        if (res.status === 200) {
          const processedData = processJSON(res.data);

          setFinding(processedData);
        } else {
          // eslint-disable-next-line no-console
          setError('Error fetching finding detail');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const findingDetailConfig: ConfigItem[] = [
    finding.finding_id !== '' && finding.finding_id !== null
      ? { name: 'Finding ID', key: 'finding_id', type: 'string' }
      : { name: 'ID', key: 'id', type: 'string' },
    // Hide endpoint, CA, sast
    finding.source_type === 'was_data' && {
      name: 'Host Instance Vuln ID',
      key: 'host_instance_vuln_id',
      type: 'string',
    },
    // Hide sast
    finding.source_type !== 'sast' && {
      name: 'QID',
      key: 'qid',
      type: 'string',
    },
    { name: 'Name', key: 'name', type: 'string' },
    // Hide CA, sast, was
    finding.source_type === 'endpoint_data' && {
      name: 'Port',
      key: 'port',
      type: 'string',
    },

    { name: 'SSL', key: 'ssl', type: 'boolean' },

    // only for CA and EPs
    (finding.source_type === 'endpoint_data' ||
      finding.source_type === 'ca_data') && {
      name: 'FQDN',
      key: 'fqdn',
      type: 'string',
    },

    { name: 'Category', key: 'category', type: 'string' },
    // Hide endpoint, CA, sast
    finding.source_type === 'was_data' && {
      name: 'URL',
      key: 'url',
      type: 'url',
    },
    // Hide endpoint, sast, was
    finding.source_type === 'ca_data' && {
      name: 'Cloud Agent',
      key: 'cloud_agent_name',
      type: 'string',
    },
    { name: 'Issue Type', key: 'issue_type', type: 'string' },
    { name: 'Finding Type', key: 'finding_type', type: 'string' },
    { name: 'Severity', key: 'severity', type: 'number' },
    {
      name: 'Softwares',
      key: 'software_list',
      type: 'list',
      listConfig: {
        childNode: null,
        config: [
          { name: 'Name', key: 'product', type: 'string' },
          { name: 'Vendor', key: 'vendor', type: 'string' },
        ],
      },
    },
    {
      name: 'Vendor Reference',
      key: 'vendor_reference_list',
      type: 'listurl',
      listConfig: { childNode: 'id', config: [] },
    },
    // hide sast
    finding.source_type !== 'sast' && {
      name: 'Threat',
      key: 'diagnosis',
      type: 'html',
    },
    // only sast
    finding.source_type === 'sast' && {
      name: 'Estimated time to fix',
      key: 'effort',
      type: 'string',
    },
    { name: 'Impact', key: 'consequence', type: 'html' },
    finding.source_type === 'sast' && {
      name: 'Code Line No.',
      key: 'line_no',
      type: 'string',
    },
    finding.source_type === 'sast' && {
      name: 'Fix',
      key: 'message',
      type: 'string',
    },
    { name: 'Solution', key: 'solution', type: 'html' },
    // hide sast, was
    finding.source_type !== 'sast' &&
      finding.source_type !== 'was_data' && {
        name: 'Results',
        key: 'results',
        type: 'html',
      },

    // only was
    ...(finding.source_type === 'was_data'
      ? [
          { name: 'Parameters', key: 'param', type: 'string' },
          {
            name: 'Payloads',
            key: 'payload.list',
            type: 'list',
            listConfig: {
              childNode: 'PayloadInstance',
              config: [
                // { name: 'Payload', key: 'payload', type: 'string' },
                {
                  name: 'Request',
                  key: 'request',
                  type: 'object',
                  config: [
                    { name: 'Link', key: 'link', type: 'url' },
                    { name: 'Method', key: 'method', type: 'string' },
                    { name: 'Headers', key: 'headers', type: 'string' },
                  ],
                },
                { name: 'Response', key: 'response', type: 'string' },
              ],
            },
          },
        ]
      : []),
    // only was
    finding.source_type === 'was_data' && {
      name: 'Access Path',
      key: 'access_path.list',
      type: 'list',
      listConfig: {
        childNode: 'Url',
        config: [{ name: 'URL Value', key: 'value', type: 'url' }],
      },
    },
    // only was
    finding.source_type === 'was_data' && {
      name: 'OWASP',
      key: 'owasp.list',
      type: 'list',
      listConfig: {
        childNode: 'OWASP',
        config: [
          { name: 'Code', key: 'code', type: 'number' },
          { name: 'Name', key: 'name', type: 'string' },
          { name: 'URL', key: 'url', type: 'url' },
        ],
      },
    },
    // only was
    finding.source_type === 'was_data' && {
      name: 'WASC',
      key: 'wasc.list',
      type: 'list',
      listConfig: {
        childNode: 'WASC',
        config: [
          { name: 'Code', key: 'code', type: 'number' },
          { name: 'Name', key: 'name', type: 'string' },
          { name: 'URL', key: 'url', type: 'url' },
        ],
      },
    },
    // hide sast
    finding.source_type !== 'sast' && {
      name: 'CVSS',
      key: 'cvss',
      type: 'object',
      config: [
        { name: 'Base', key: 'base', type: 'string' },
        { name: 'Temporal', key: 'temporal', type: 'string' },
        { name: 'Vector String', key: 'vector_string', type: 'string' },
        {
          name: 'Access',
          key: 'access',
          type: 'object',
          config: [
            { name: 'Vector', key: 'vector', type: 'string' },
            { name: 'Complexity', key: 'complexity', type: 'string' },
          ],
        },
        {
          name: 'Impact',
          key: 'impact',
          type: 'object',
          config: [
            { name: 'Confidentiality', key: 'confidentiality', type: 'string' },
            { name: 'Integrity', key: 'integrity', type: 'string' },
            { name: 'Availability', key: 'availability', type: 'string' },
          ],
        },
        { name: 'Authentication', key: 'authentication', type: 'string' },
        { name: 'Exploitability', key: 'exploitability', type: 'string' },
        { name: 'Remediation Level', key: 'remediation_level', type: 'string' },
        { name: 'Report Confidence', key: 'report_confidence', type: 'string' },
      ],
    },
    // hide sast, was
    finding.source_type !== 'sast' &&
      finding.source_type !== 'was_data' && {
        name: 'CVSS V3',
        key: 'cvss_v3',
        type: 'object',
        config: [
          { name: 'Base', key: 'base', type: 'string' },
          { name: 'Temporal', key: 'temporal', type: 'string' },
          { name: 'Vector String', key: 'vector_string', type: 'string' },
          {
            name: 'Attack',
            key: 'attack',
            type: 'object',
            config: [
              { name: 'Vector', key: 'vector', type: 'string' },
              { name: 'Complexity', key: 'complexity', type: 'string' },
            ],
          },
          {
            name: 'Impact',
            key: 'impact',
            type: 'object',
            config: [
              {
                name: 'Confidentiality',
                key: 'confidentiality',
                type: 'string',
              },
              { name: 'Integrity', key: 'integrity', type: 'string' },
              { name: 'Availability', key: 'availability', type: 'string' },
            ],
          },
          {
            name: 'Privileges Required',
            key: 'privileges_required',
            type: 'string',
          },
          { name: 'User Interaction', key: 'user_interaction', type: 'string' },
          { name: 'Scope', key: 'scope', type: 'string' },
          {
            name: 'Exploit Code Maturity',
            key: 'exploit_code_maturity',
            type: 'string',
          },
          {
            name: 'Remediation Level',
            key: 'remediation_level',
            type: 'string',
          },
          {
            name: 'Report Confidence',
            key: 'report_confidence',
            type: 'string',
          },
        ],
      },
    {
      name: 'CVE LIST',
      key: 'cve_list',
      type: 'listurl',
      listConfig: { childNode: 'id', config: [] },
    },
    {
      name: 'Bugtraq LIST',
      key: 'bugtraq_list',
      type: 'listurl',
      listConfig: { childNode: 'id', config: [] },
    },
    // only was
    finding.source_type === 'was_data' && {
      name: 'CWE',
      key: 'cwe',
      type: 'listurl',
    },
    // only was
    finding.source_type === 'was_data' && {
      name: 'Web App',
      key: 'web_app',
      type: 'object',
      config: [
        { name: 'ID', key: 'id', type: 'string' },
        { name: 'Name', key: 'name', type: 'string' },
        { name: 'URL', key: 'url', type: 'url' },
      ],
    },
    // only endpoint
    finding.source_type === 'endpoint_data' && {
      name: 'Endpoint',
      key: 'endpoint_name',
      type: 'string',
    },
    // hide sast, was
    finding.source_type !== 'sast' &&
      finding.source_type !== 'was_data' && {
        name: 'Endpoint IP',
        key: 'endpoint_ip',
        type: 'string',
      },
    // on ca
    finding.source_type === 'ca_data' && {
      name: 'Patchable',
      key: 'patchable',
      type: 'string',
    },
    { name: 'PCI Vulnerability', key: 'pci_flag', type: 'string' },
    { name: 'Status', key: 'status', type: 'string' },
    { name: 'Age', key: 'age', type: 'string' },
    { name: 'First Detected Date', key: 'first_detected_date', type: 'date' },
    { name: 'Last Detected Date', key: 'last_detected_date', type: 'date' },
    { name: 'Last Tested Date', key: 'last_tested_date', type: 'date' },

    finding.first_reopened_datetime && {
      name: 'First Reopened Date',
      key: 'first_reopened_datetime',
      type: 'date',
    },
    finding.last_reopened_datetime && {
      name: 'Last Reopened Date',
      key: 'last_reopened_datetime',
      type: 'date',
    },
    finding.times_reopened && {
      name: 'Times Reopened',
      key: 'times_reopened',
      type: 'number',
    },
    finding.last_fixed_datetime && {
      name: 'Last Fixed Date',
      key: 'last_fixed_datetime',
      type: 'date',
    },
    {
      name: 'Ignored',
      key: 'ignore_message',
      type: 'html',
    },
  ].filter(
    (item) =>
      item !== null && item !== '' && item !== undefined && item !== false,
  );

  const capitalizeWords = (str) => {
    return str.toLowerCase().replace(/(?:^|\s)\w/g, function (word) {
      return word.toUpperCase();
    });
  };

  return (
    <ModalDialog
      title={`${
        finding.issue_type
          ? capitalizeWords(finding.issue_type)
          : 'Vulnerability'
      }  Detail`}
      subTitle={finding['name'] || ''}
      childComponent={
        <CustomModalWrapper id="finding-scroll">
          <Row>
            <Col>
              {error && <div>{error}</div>}
              {loading && <LoadingScreen />}
              {!error && !loading && !finding.id && <div>No data found</div>}
              {!error && !loading && finding && (
                <DetailTable data={finding} config={findingDetailConfig} />
              )}
            </Col>
          </Row>
        </CustomModalWrapper>
      }
      onClose={() => onClose()}
    />
  );
};

export default FindingDetailModal;
